import { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;

const CompanyDetails = ({ data, onSave, isLoading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (data) {
      setForm({
        company_name: data.company_name || '',
        company_address: data.company_address || '',
        company_phone: data.company_phone || '',
        company_email: data.company_email || '',
      });
      setErrors({});
    }
  }, [data]);

  const validate = () => {
    const newErrors = {};
    if (!form.company_name.trim()) {
      newErrors.company_name = 'Company name is required';
    }
    if (!form.company_address.trim()) {
      newErrors.company_address = 'Company address is required';
    }
    if (!form.company_phone.trim()) {
      newErrors.company_phone = 'Phone number is required';
    } else if (!PHONE_REGEX.test(form.company_phone.trim())) {
      newErrors.company_phone = 'Phone number must be exactly 10 digits';
    }
    if (!form.company_email.trim()) {
      newErrors.company_email = 'Email is required';
    } else if (!EMAIL_REGEX.test(form.company_email.trim())) {
      newErrors.company_email = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (data) {
      setForm({
        company_name: data.company_name || '',
        company_address: data.company_address || '',
        company_phone: data.company_phone || '',
        company_email: data.company_email || '',
      });
    }
    setErrors({});
    setIsEditing(false);
  };

  const renderValue = (value) => {
    if (!value) {
      return <span className="text-gray-400 italic">Not provided</span>;
    }
    return value;
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 mb-8">
      {/* Header Section */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center space-x-2">
              <Icon name="Building" size={20} className="sm:w-6 sm:h-6 text-gray-700" />
              <span>Company Details</span>
            </h2>
            <p className="text-sm text-gray-500 mt-1">Configure company information</p>
          </div>
          {isEditing ? (
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 w-1/2 sm:w-auto text-sm font-medium"
              >
                Cancel
              </button>
              <Button
                onClick={handleSave}
                loading={isLoading}
                disabled={isLoading}
                iconName="Check"
                className="w-1/2 sm:w-auto"
              >
                Save
              </Button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 w-full sm:w-auto text-sm font-medium"
            >
              <Icon name="Edit3" size={16} />
              <span>Edit Details</span>
            </button>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="p-2 sm:p-4">
        {isEditing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Row 1: Company Profile (Edit) - Spans both columns on md screens */}
            <div className="md:col-span-2 p-4 bg-gray-50/50 border border-gray-200 rounded-xl space-y-4">
              <div className="flex items-center space-x-2 border-b border-gray-200 pb-2 mb-1">
                <Icon name="Building2" size={18} className="text-gray-500" />
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Company Identity & Location
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Company Name */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Company Name
                  </label>
                  <div className="relative rounded-lg shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <Icon name="Building2" size={18} />
                    </div>
                    <input
                      type="text"
                      value={form.company_name}
                      onChange={(e) => handleChange('company_name', e.target.value)}
                      className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${
                        errors.company_name
                          ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter company name"
                    />
                  </div>
                  {errors.company_name && (
                    <p className="text-xs text-red-500 mt-1 flex items-center space-x-1">
                      <Icon name="AlertCircle" size={12} className="text-red-500" />
                      <span>{errors.company_name}</span>
                    </p>
                  )}
                </div>

                {/* Address */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Address
                  </label>
                  <div className="relative rounded-lg shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                      <Icon name="MapPin" size={18} />
                    </div>
                    <input
                      type="text"
                      value={form.company_address}
                      onChange={(e) => handleChange('company_address', e.target.value)}
                      className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${
                        errors.company_address
                          ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter address"
                    />
                  </div>
                  {errors.company_address && (
                    <p className="text-xs text-red-500 mt-1 flex items-center space-x-1">
                      <Icon name="AlertCircle" size={12} className="text-red-500" />
                      <span>{errors.company_address}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Phone Input - Spans 1 column */}
            <div className="p-4 bg-gray-50/50 border border-gray-200 rounded-xl space-y-1.5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Phone
              </label>
              <div className="relative rounded-lg shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <Icon name="Phone" size={18} />
                </div>
                <input
                  type="text"
                  value={form.company_phone}
                  onChange={(e) => handleChange('company_phone', e.target.value)}
                  className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${
                    errors.company_phone
                      ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter phone number"
                />
              </div>
              {errors.company_phone && (
                <p className="text-xs text-red-500 mt-1 flex items-center space-x-1">
                  <Icon name="AlertCircle" size={12} className="text-red-500" />
                  <span>{errors.company_phone}</span>
                </p>
              )}
            </div>

            {/* Row 2: Email Input - Spans 1 column */}
            <div className="p-4 bg-gray-50/50 border border-gray-200 rounded-xl space-y-1.5">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Email
              </label>
              <div className="relative rounded-lg shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <Icon name="Mail" size={18} />
                </div>
                <input
                  type="email"
                  value={form.company_email}
                  onChange={(e) => handleChange('company_email', e.target.value)}
                  className={`block w-full pl-10 pr-3 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all ${
                    errors.company_email
                      ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter email address"
                />
              </div>
              {errors.company_email && (
                <p className="text-xs text-red-500 mt-1 flex items-center space-x-1">
                  <Icon name="AlertCircle" size={12} className="text-red-500" />
                  <span>{errors.company_email}</span>
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Row 1: Company Profile (Name + Address) - Spans both columns */}
            <div className="md:col-span-2 flex items-start space-x-4 p-4 bg-gray-50 rounded-md  ">
              {/* <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 text-primary-600 shrink-0 group-hover:scale-110 transition-transform duration-200">
                <Icon name="Building2" size={20} />
              </div> */}
              <div className="flex-1 min-w-0">
                <h4 className="text-base font-bold text-lg text-gray-900 truncate">
                  {renderValue(data?.company_name)}
                </h4>
                <div className="mt-2 pt-2 border-t border-gray-200/50 flex items-start space-x-1.5 text-sm text-gray-600">
                  <Icon name="MapPin" size={16} className="mt-0.5 shrink-0 text-gray-400" />
                  <span className="break-words">{renderValue(data?.company_address)}</span>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-200/50 flex items-start space-x-1.5 text-sm text-gray-600">
                  <Icon name="Phone" size={16} className="mt-0.5 shrink-0 text-gray-400" />
                  <span className="break-words">{renderValue(data?.company_phone)}</span>
                </div>
                <div className="mt-2 pt-2 border-t border-gray-200/50 flex items-start space-x-1.5 text-sm text-gray-600">
                  <Icon name="Mail" size={16} className="mt-0.5 shrink-0 text-gray-400" />
                  <span className="break-words">{renderValue(data?.company_email)}</span>
                </div>
              </div>
            </div>

            {/* Row 2: Phone */}
            {/* <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-sm transition-all duration-200 group">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 text-primary-600 shrink-0 group-hover:scale-110 transition-transform duration-200">
                <Icon name="Phone" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block">
                  Phone
                </span>
                <span className="text-sm font-semibold text-gray-900 block mt-1 break-words">
                  {renderValue(data?.company_phone)}
                </span>
              </div>
            </div> */}

            {/* Row 2: Email */}
            {/* <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-sm transition-all duration-200 group">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 text-primary-600 shrink-0 group-hover:scale-110 transition-transform duration-200">
                <Icon name="Mail" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block">
                  Email
                </span>
                <span className="text-sm font-semibold text-gray-900 block mt-1 break-words">
                  {renderValue(data?.company_email)}
                </span>
              </div>
            </div> */}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyDetails;
